/** Every navigable path in one place, so links are never stringly-typed. */
export const ROUTES = {
  home: '/',
  team: '/team',
  contests: '/contests',
  transfers: '/transfers',
  fixtures: '/fixtures',
  leagues: '/leagues',
  leaderboard: '/leaderboard',
  scoring: '/scoring',
  profile: '/profile',
  login: '/login',
  /**
   * Where the API sends the browser back after Google sign-in. Must be listed
   * in the API's `AUTH_REDIRECT_ALLOWLIST`, and match `AUTH_SUCCESS_REDIRECT`
   * for the case where the flow finishes without a `redirectTo`.
   */
  authGoogle: '/auth/google',
  onboarding: '/onboarding',
} as const;

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/** Build the entry screen's path for one contest. */
export const contestBuildPath = (contestId: string): string => `/contests/${contestId}/build`;

/** The match centre for one fixture. */
export const matchCentrePath = (fixtureId: number): string => `/fixtures/${fixtureId}`;

/** My Team for one contest entry. */
export const teamPath = (teamId: string): string => `/team/${teamId}`;

/** Transfers for one contest entry. */
export const transfersPath = (teamId: string): string => `/transfers/${teamId}`;

/**
 * Which icon a nav item draws.
 *
 * A name rather than the component itself, so this module stays free of JSX
 * imports — it is pulled in by the auth store and the router, neither of which
 * should drag the icon set along with it.
 */
export type NavIconName =
  | 'home'
  | 'team'
  | 'contests'
  | 'fixtures'
  | 'leagues'
  | 'leaderboard'
  | 'points'
  | 'profile';

export interface NavItem {
  to: RoutePath;
  label: string;
  /** Shorter label used on the mobile bottom bar. */
  shortLabel?: string;
  icon: NavIconName;
  /** Whether the item appears in the mobile bottom bar. */
  onBottomBar: boolean;
}

/**
 * Primary navigation.
 *
 * Transfers is deliberately absent: a manager can hold an eleven in several
 * contests at once, so "transfers" is not one destination — it only means
 * something once a contest is named. It is reached from the team it belongs
 * to instead. Profile is absent too; it lives in the header avatar.
 *
 * Everything not on the bottom bar stays reachable on mobile from the profile
 * screen, which the avatar opens.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  { to: ROUTES.home, label: 'Home', icon: 'home', onBottomBar: true },
  { to: ROUTES.team, label: 'My Team', shortLabel: 'Team', icon: 'team', onBottomBar: true },
  { to: ROUTES.contests, label: 'Contests', icon: 'contests', onBottomBar: true },
  { to: ROUTES.fixtures, label: 'Fixtures', icon: 'fixtures', onBottomBar: true },
  { to: ROUTES.leagues, label: 'Leagues', icon: 'leagues', onBottomBar: true },
  { to: ROUTES.leaderboard, label: 'Leaderboard', icon: 'leaderboard', onBottomBar: false },
  {
    to: ROUTES.scoring,
    label: 'How points work',
    shortLabel: 'Points',
    icon: 'points',
    onBottomBar: false,
  },
];
